// The WebGL backend.
//
// It owns a retained scene of `SceneNode`s presenting the same API the map's
// drawing code already used on SVG elements, and draws that scene onto a canvas.
//
// The unit of work is a *batch*: one direct child of a layer — in practice one
// map object — with all of its descendants' transforms folded into its vertices.
// The camera and the object's own placement stay uniforms, so the two things that
// change every frame (the map easing towards its target scale, and objects easing
// towards their laid-out positions) cost two uniform writes per object rather
// than any geometry work at all. A batch is rebuilt only when something inside it
// changes, and the tessellation that rebuild needs is memoised across the estate.
//
// That is the property the SVG renderer could not have: its cost was proportional
// to how many elements existed, not to how many changed.

import { SceneNode } from "../scene/node";
import {
  identity,
  multiply,
  invert,
  parseTransform,
  applyX,
  applyY,
} from "../scene/matrix";
import {
  resolveStyle,
  isHidden,
  ROOT_STYLE,
  setBaseFontFrom,
} from "../scene/style";
import { VertexBuffer, VERTEX_STRIDE, FLOATS_PER_VERTEX } from "./vertexBuffer";
import { Atlas } from "./atlas";
import { ImageStore } from "./images";
import { createProgram } from "./program";
import { effects, lockedFocus } from "../effects";
import { DepthOfField } from "./dof";
import { emitNode, drawsChildren, num } from "./geometry";
import { flattenPath } from "./path";
import { triangulate } from "./tess";
import { computeBBox } from "./bounds";
import { hitTestNode, dispatch, dispatchDirect } from "./pick";
import { resetGlyphCache } from "./text";
import perf from "../../perf";

// Device pixels per CSS pixel the map renders at, over and above the display's
// own ratio. Two is the point where hairlines stop breaking up; beyond that the
// returns fall away quickly. Override with ?ss=N.
const SUPERSAMPLE = 2;

// Ceiling on the framebuffer, so a large window on a modest GPU steps back
// towards the display's native resolution rather than asking for something it
// cannot fill in a frame.
const MAX_BACKING_PIXELS = 12e6;

// How quickly the depth of field comes and goes, as a fraction of the remaining
// distance per frame — about a third of a second either way.
const DOF_EASE = 0.09;

const BLEND_NORMAL = 0;
const BLEND_DIFFERENCE = 1;

// Attributes that only feed a uniform when they change on a batch's own root, so
// setting them does not invalidate its geometry. These are exactly the ones the
// frame loop writes constantly - `data-flip` every frame of an env card's page
// turn.
const BATCH_UNIFORM_ATTRS = {
  transform: true,
  opacity: true,
  display: true,
  visibility: true,
  "data-flip": true,
};

// Viewer distance for the `data-flip` perspective, in batch-space units. The
// env card is ~100 units wide, so this is about two and a half card-widths
// away - the strength of a typical CSS `perspective` on a flipping tile.
const FLIP_PERSPECTIVE = 250;

// A node's parsed transform, cached on the node against the exact string. The
// global cache in matrix.js thrashes once the estate is large: the idle spin
// gives a thousand objects a fresh `translate(...)` every frame, which clears
// it wholesale and re-parses everything else too. Layout may pre-fill the two
// fields when it already has the numbers, which skips the parse entirely.
function transformOf(node) {
  const str = node.attrs.transform;
  if (!str) return null;
  if (node._parsedTfFor === str) return node._parsedTf;
  const m = parseTransform(str);
  node._parsedTfFor = str;
  node._parsedTf = m;
  return m;
}

class Batch {
  constructor(gl, root) {
    this.gl = gl;
    this.root = root;
    this.buffer = new VertexBuffer(64);
    this.glBuffer = gl.createBuffer();
    this.vao = null;
    this.capacityBytes = 0;
    this.ranges = [];
    this.picks = [];
    // Where re-emitted geometry goes during an in-place update; the batch's own
    // pick list is unaffected because the same nodes come back in the same order.
    this.pickSink = null;
    this.bounds = null;
    this.matrix = null;

    // A full rebuild is pending.
    this.dirty = true;
    // Individual nodes whose geometry can be re-emitted into their existing slice.
    this.dirtyNodes = [];
    // Vertex range awaiting upload; -1 means nothing, 0..length means all.
    this.uploadFrom = 0;
    this.uploadTo = 0;

    // Clip shapes used by this batch, drawn into the stencil buffer rather than
    // painted. Their geometry lives at the end of the same vertex buffer.
    this.clips = [];
  }

  markUpload(from, to) {
    if (this.uploadTo === 0) {
      this.uploadFrom = from;
      this.uploadTo = to;
      return;
    }
    if (from < this.uploadFrom) this.uploadFrom = from;
    if (to > this.uploadTo) this.uploadTo = to;
  }

  destroy(vaoExt) {
    if (this.glBuffer) {
      this.gl.deleteBuffer(this.glBuffer);
      this.glBuffer = null;
    }
    if (this.vao && vaoExt) {
      vaoExt.destroy(this.vao);
      this.vao = null;
    }
    this.picks.length = 0;
    this.ranges.length = 0;
    this.dirtyNodes.length = 0;
  }
}

// Shared by every in-place update; the pick entries it collects are discarded.
const scratchPicks = [];

// Why in-place updates fall back to a full rebuild, which is the difference
// between a batch costing two uniform writes and costing a full retessellation.
// The counts surface in the perf HUD; the per-node log costs an allocation per
// bail-out and is only kept while `window.mapDiag.record` is set from the
// console.
const DIAG_LOG_LIMIT = 500;

const updateDiag = {
  countMismatch: 0,
  clipChange: 0,
  ok: 0,
  record: false,
  log: [],
  reset() {
    this.countMismatch = this.clipChange = this.ok = 0;
    this.log.length = 0;
  },
};

function diagNodeName(batch, node) {
  const path = batch.root.attrs["data-path"] || batch.root.attrs.id || "?";
  const props = node.attrs["data-props"];
  return path + "/" + node.tagName + (props ? " " + props : "");
}

function noteBailOut(batch, node, reason, expected, produced) {
  if (reason === "count") updateDiag.countMismatch++;
  else updateDiag.clipChange++;

  if (!updateDiag.record) return;
  if (updateDiag.log.length >= DIAG_LOG_LIMIT) updateDiag.log.shift();
  updateDiag.log.push({
    t: Math.round(performance.now()),
    node: diagNodeName(batch, node),
    reason,
    expected,
    produced,
  });
}

if (typeof window !== "undefined") {
  // Alongside window.map, window.camera and window.perf.
  window.mapDiag = updateDiag;
}

export class WebGLRenderer {
  constructor(container) {
    this.container = container;
    this.kind = "webgl";

    this.canvas = document.createElement("canvas");
    this.canvas.id = "map";
    this.canvas.className = "map-canvas";
    container.appendChild(this.canvas);

    this.gl = createContext(this.canvas);
    if (!this.gl) {
      container.removeChild(this.canvas);
      throw new Error("WebGL is not available");
    }

    this.handleContextLost = this.handleContextLost.bind(this);
    this.handleContextRestored = this.handleContextRestored.bind(this);
    this.handleMouseMove = this.handleMouseMove.bind(this);
    this.handlePointerEvent = this.handlePointerEvent.bind(this);
    this.handleMouseOut = this.handleMouseOut.bind(this);

    this.lost = false;
    this.canvas.addEventListener("webglcontextlost", this.handleContextLost);
    this.canvas.addEventListener(
      "webglcontextrestored",
      this.handleContextRestored
    );

    this.width = 0;
    this.height = 0;
    this.canvasRect = null;
    this.currentBlend = -1;
    this.superSample = readSuperSample();

    this.root = new SceneNode("svg", this);
    this.buildScene();

    setBaseFontFrom(container);
    this.initGL();

    this.hoverChain = [];
    this.bindPointerEvents();

    // What the renderer's cost is made of. Batches drawn is the draw-call count;
    // rebuilt is how many had to have their geometry regenerated, which is the
    // number that should stay small while the map is merely moving.
    this.stats = { batches: 0, rebuilt: 0, updated: 0, vertices: 0 };
    perf.counter("batches", () => this.stats.batches);
    perf.counter("rebuilt", () => this.stats.rebuilt);
    perf.counter("updated", () => this.stats.updated);
    perf.counter("vertices", () => this.stats.vertices);
    perf.counter("bail/count", () => updateDiag.countMismatch);
    perf.counter("bail/changes", () => updateDiag.clipChange);
  }

  // -------------------------------------------------------------------------
  // Skeleton
  // -------------------------------------------------------------------------

  // Mirrors the layer structure of the SVG renderer, because document order
  // between the layers is what puts connection lines under the objects and the
  // callouts over them.
  buildScene() {
    const root = this.root;

    this.defs = root.append("defs");

    this.parallax = root.append("g");
    this.parallax.setAttribute("transform", "scale(0.5)");
    this.parallax.setAttribute("opacity", "0.5");

    this.lines = root.append("g");
    this.lines.setAttribute("id", "lines");
    this.regions = this.lines.append("g");
    this.regions.setAttribute("id", "regions");
    this.lineGroup = this.lines.append("g");
    this.lineGroup.setAttribute("stroke", "#888");

    this.underlay = root.append("g");
    this.underlay.setAttribute("id", "underlay");

    this.main = root.append("g");
    this.main.setAttribute("id", "main");

    this.overlay = root.append("g");
    this.overlay.setAttribute("id", "overlay");

    this.statusText = this.overlay.append("text");
    this.statusText.setAttribute("font-size", "100");
    this.statusText.setAttribute("fill", "white");
    this.statusText.setAttribute("text-anchor", "middle");
    this.statusText.setAttribute("dominant-baseline", "middle");
    this.statusText.setAttribute("font-weight", "bold");
    this.statusText.text("Initialising...");

    // Layers are the level at which the camera applies; their children are the
    // batches.
    this.layers = [
      this.parallax,
      this.lines,
      this.underlay,
      this.main,
      this.overlay,
    ];
    for (let i = 0; i < this.layers.length; i++) this.layers[i].isLayer = true;
  }

  // -------------------------------------------------------------------------
  // GL resources
  // -------------------------------------------------------------------------

  initGL() {
    const gl = this.gl;

    const isWebGL2 =
      typeof WebGL2RenderingContext !== "undefined" &&
      gl instanceof WebGL2RenderingContext;
    const hasDerivatives =
      isWebGL2 || !!gl.getExtension("OES_standard_derivatives");

    if (isWebGL2) {
      this.vaoExt = {
        create: () => gl.createVertexArray(),
        bind: (vao) => gl.bindVertexArray(vao),
        destroy: (vao) => gl.deleteVertexArray(vao),
      };
    } else {
      const ext = gl.getExtension("OES_vertex_array_object");
      this.vaoExt = ext
        ? {
            create: () => ext.createVertexArrayOES(),
            bind: (vao) => ext.bindVertexArrayOES(vao),
            destroy: (vao) => ext.deleteVertexArrayOES(vao),
          }
        : null;
    }

    this.shader = createProgram(gl, isWebGL2, hasDerivatives);
    this.atlas = new Atlas(gl);
    this.images = new ImageStore(this.atlas, () => this.markAllDirty());

    this.emitContext = {
      atlas: this.atlas,
      images: this.images,
      lookupId: (id) => this.lookupId(id),
    };

    gl.disable(gl.DEPTH_TEST);
    gl.disable(gl.CULL_FACE);
    gl.enable(gl.BLEND);
    gl.clearColor(0, 0, 0, 0);
    this.currentBlend = -1;
  }

  handleContextLost(event) {
    event.preventDefault();
    this.lost = true;
    console.warn(
      "[borg-map] WebGL context lost; drawing will resume if it returns"
    );
  }

  handleContextRestored() {
    // Everything on the GPU is gone, including the atlas that the glyph and image
    // caches hold slots into, so those have to go with it.
    resetGlyphCache();
    this.initGL();
    this.forEachBatch((batch) => {
      batch.glBuffer = this.gl.createBuffer();
      batch.capacityBytes = 0;
      batch.dirty = true;
    });
    this.setViewport(this.width, this.height);
    this.lost = false;
  }

  // -------------------------------------------------------------------------
  // Scene callbacks
  // -------------------------------------------------------------------------

  nodeChanged(node, attribute) {
    const batchRoot = batchRootOf(node);
    if (!batchRoot) return;

    // A batch's own placement and opacity feed uniforms rather than vertices.
    if (batchRoot === node && BATCH_UNIFORM_ATTRS[attribute]) return;

    const batch = batchRoot._batch;
    if (!batch || batch.dirty) return;

    // One node changing does not mean the whole object has to be regenerated.
    // This matters because something inside almost every object changes almost
    // every frame — the RAG badge is re-placed whenever the object or its parent
    // moves — so without this the batching would buy nothing while the map is in
    // motion, which is most of the time.
    if (batch.dirtyNodes.indexOf(node) < 0) batch.dirtyNodes.push(node);
  }

  structureChanged(parent) {
    const batchRoot = batchRootOf(parent);
    if (batchRoot && batchRoot._batch) batchRoot._batch.dirty = true;
  }

  nodeDetached(node) {
    if (node._batch) {
      node._batch.destroy(this.vaoExt);
      node._batch = null;
    }
  }

  measure(node) {
    return computeBBox(node);
  }

  markAllDirty() {
    this.forEachBatch((batch) => {
      batch.dirty = true;
    });
  }

  forEachBatch(fn) {
    for (let i = 0; i < this.layers.length; i++) {
      const children = this.layers[i].children;
      for (let c = 0; c < children.length; c++)
        if (children[c]._batch) fn(children[c]._batch);
    }
  }

  lookupId(id) {
    // Referenced content lives in <defs>; the set is tiny and static.
    return this.defs.select("#" + id);
  }

  // -------------------------------------------------------------------------
  // Viewport
  // -------------------------------------------------------------------------

  setViewport(width, height) {
    this.width = width;
    this.height = height;
    this.canvasRect = null;

    const scale = this.renderScale(width, height);
    const backingWidth = Math.max(1, Math.round(width * scale));
    const backingHeight = Math.max(1, Math.round(height * scale));

    if (this.canvas.width !== backingWidth) this.canvas.width = backingWidth;
    if (this.canvas.height !== backingHeight)
      this.canvas.height = backingHeight;

    this.canvas.style.width = width + "px";
    this.canvas.style.height = height + "px";

    if (this.gl) this.gl.viewport(0, 0, backingWidth, backingHeight);
  }

  // Device pixels per CSS pixel to render at.
  //
  // The multisampling the context asks for resolves coverage in four steps,
  // which is not enough for the map's hairlines: a connection line is half a
  // user unit wide and the camera is often well below 1:1, so the line covers a
  // fraction of a pixel and four samples quantise it into a dotted trail. SVG
  // computes exact coverage and draws a continuous faint line instead.
  //
  // Rendering above the display's own resolution and letting the compositor
  // scale down recovers that: at twice the size, with multisampling on top, each
  // CSS pixel is resolved from an order of magnitude more samples. It applies to
  // everything — fills, joins and caps as well as strokes — without any
  // per-primitive work, which the alternative of analytic coverage per shape
  // would have needed for each of them separately.
  renderScale(width, height) {
    const ratio = window.devicePixelRatio || 1;

    // A display that is already dense is doing this for us.
    let scale = Math.max(ratio, this.superSample);

    // Very large windows would otherwise ask for a framebuffer the driver
    // refuses or cannot fill in time; fall back towards the native ratio.
    const maxDimension = this.gl
      ? this.gl.getParameter(this.gl.MAX_RENDERBUFFER_SIZE)
      : 4096;

    while (
      scale > ratio &&
      (width * scale > maxDimension ||
        height * scale > maxDimension ||
        width * height * scale * scale > MAX_BACKING_PIXELS)
    ) {
      scale -= 0.25;
    }

    return Math.max(scale, ratio);
  }

  // Runtime control, so the cost can be traded against quality on a machine
  // where it matters without a rebuild.
  setSuperSample(factor) {
    this.superSample = Math.max(1, Math.min(4, factor));
    this.setViewport(this.width, this.height);
  }

  // -------------------------------------------------------------------------
  // Drawing
  // -------------------------------------------------------------------------

  draw() {
    const gl = this.gl;
    if (!gl || this.lost || !this.width || !this.height) return;

    // Everything from here to the composite at the bottom goes to a texture
    // rather than the screen when something is locked and depth of field is on.
    //
    // Eased in and out rather than switched, and the last known focus is kept so
    // the blur has somewhere to be while it is fading back out after a target is
    // released.
    const focus = effects.depthOfField ? lockedFocus() : null;
    if (focus) this.lastFocus = { x: focus.x, y: focus.y, r: focus.r };

    const want = focus ? 1 : 0;
    this.dofAmount = this.dofAmount || 0;
    this.dofAmount += (want - this.dofAmount) * DOF_EASE;
    if (Math.abs(want - this.dofAmount) < 0.004) this.dofAmount = want;

    let offscreen = false;

    if (this.dofAmount > 0 && this.lastFocus) {
      if (!this.dof) {
        try {
          this.dof = new DepthOfField(gl);
        } catch (e) {
          console.warn("[borg-map] depth of field unavailable:", e);
          this.dof = null;
          effects.depthOfField = false;
        }
      }
      if (this.dof) {
        offscreen = this.dof.begin(
          gl.drawingBufferWidth,
          gl.drawingBufferHeight
        );
      }
    }

    gl.clear(gl.COLOR_BUFFER_BIT | gl.STENCIL_BUFFER_BIT);
    this.beginSceneProgram();

    this.stats.batches = 0;
    this.stats.rebuilt = 0;
    this.stats.updated = 0;
    this.stats.vertices = 0;

    for (let i = 0; i < this.layers.length; i++) {
      const layer = this.layers[i];
      // The overlay carries the callouts and the selection reticle, which are
      // interface rather than scene: blurring the labels being read, and the ring
      // showing what is under the pointer, is the wrong side of the trade. It is
      // drawn after the composite instead.
      if (offscreen && layer === this.overlay) continue;
      this.drawLayer(layer);
    }

    // Back to the screen, through the blur. Leaves the shader unbound, which is
    // why anything drawn afterwards has to set the program up again.
    if (offscreen) {
      this.dof.end(this.lastFocus, this.width, this.height, this.dofAmount);

      // The stencil belongs to whichever framebuffer is bound, and the one that
      // was cleared this frame was the offscreen target — so a clipped range in
      // the overlay would otherwise test against whatever the default
      // framebuffer's stencil last held.
      gl.clear(gl.STENCIL_BUFFER_BIT);

      this.beginSceneProgram();
      this.drawLayer(this.overlay);
    }
  }

  // Shader, camera and atlas state shared by every batch in a frame.
  beginSceneProgram() {
    const gl = this.gl;
    gl.useProgram(this.shader.program);

    const u = this.shader.uniforms;
    gl.uniform2f(u.viewScale, 2 / this.width, -2 / this.height);

    // Seconds since the page loaded, wrapped every ten minutes. Left to run it
    // would eventually grow past where a 32-bit float can resolve one frame from
    // the next, and the animations it drives would visibly quantise.
    gl.uniform1f(u.time, (performance.now() * 0.001) % 600);

    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, this.atlas.texture);
    gl.uniform1i(u.atlas, 0);

    // Last-written values of the per-batch uniforms that rarely differ between
    // batches - almost every batch is unflipped and fully opaque, so writing
    // them a thousand times a frame was pure overhead. NaN forces the first
    // write of the frame.
    this.lastFlipX = NaN;
    this.lastFlipY = NaN;
    this.lastFlipZ = NaN;
    this.lastAlpha = NaN;
  }

  drawLayer(layer) {
    if (isHidden(layer)) return;

    const gl = this.gl;
    const layerStyle = resolveStyle(layer, ROOT_STYLE);
    const layerMatrix = transformOf(layer) || identity();

    // Batches are built against a style with the layer's opacity removed; it
    // multiplies back in through the uniform instead.
    const batchBaseStyle =
      layerStyle.opacity === 1
        ? layerStyle
        : Object.assign({}, layerStyle, { opacity: 1 });

    const children = layer.children;
    for (let c = 0; c < children.length; c++) {
      const node = children[c];
      if (isHidden(node)) continue;

      const batch = node._batch || (node._batch = new Batch(gl, node));

      if (batch.dirty || batch.dirtyNodes.length) {
        perf.begin("rebuild");
        try {
          this.refreshBatch(batch, batchBaseStyle);
        } finally {
          perf.end("rebuild");
        }
      }

      const own = transformOf(node);
      batch.matrix = own ? multiply(layerMatrix, own) : layerMatrix;

      if (!batch.buffer.length) continue;

      const alpha = layerStyle.opacity * numberOr(node.attrs.opacity, 1);
      if (alpha <= 0) continue;

      this.stats.batches++;
      this.stats.vertices += batch.buffer.length;
      this.drawBatch(batch, batch.matrix, alpha);
    }
  }

  drawBatch(batch, matrix, alpha) {
    const gl = this.gl;
    const u = this.shader.uniforms;
    const a = this.shader.attributes;

    // A vertex array object records the attribute layout once per batch, saving
    // four state calls per object per frame.
    if (batch.vao) {
      this.bindVao(batch.vao);
      if (batch.uploadTo > 0) {
        gl.bindBuffer(gl.ARRAY_BUFFER, batch.glBuffer);
        if (this.uploadBatch(batch)) setAttributes(gl, a);
      }
    } else {
      batch.vao = this.createVao();
      this.bindVao(batch.vao);
      gl.bindBuffer(gl.ARRAY_BUFFER, batch.glBuffer);
      this.uploadBatch(batch);
      setAttributes(gl, a);
    }

    gl.uniform3f(u.row0, matrix[0], matrix[2], matrix[4]);
    gl.uniform3f(u.row1, matrix[1], matrix[3], matrix[5]);

    if (alpha !== this.lastAlpha) {
      this.lastAlpha = alpha;
      gl.uniform1f(u.alpha, alpha);
    }

    // The flip is a perspective projection, which the affine pipeline cannot
    // express, so it rides to the shader as its own uniform - a turning card
    // costs no geometry work at all, and its stencil clip turns with it. The
    // shrink keeps the edge nearing the viewer, which perspective magnifies,
    // inside the card's resting silhouette, so a turning card cannot poke over
    // its neighbours and content clipped to its own size is not cropped flat.
    const flip = batch.root.attrs["data-flip"];
    let fx = 1;
    let fy = 1;
    let fz = 0;
    if (flip !== undefined && batch.bounds) {
      const rad = ((parseFloat(flip) || 0) * Math.PI) / 180;
      const sin = Math.sin(rad);
      const extent = Math.max(
        Math.abs(batch.bounds.minX),
        Math.abs(batch.bounds.maxX)
      );
      const shrink =
        Math.max(0, FLIP_PERSPECTIVE - extent * Math.abs(sin)) /
        FLIP_PERSPECTIVE;
      fx = Math.cos(rad) * shrink;
      fy = shrink;
      fz = sin / FLIP_PERSPECTIVE;
    }
    if (
      fx !== this.lastFlipX ||
      fy !== this.lastFlipY ||
      fz !== this.lastFlipZ
    ) {
      this.lastFlipX = fx;
      this.lastFlipY = fy;
      this.lastFlipZ = fz;
      gl.uniform3f(u.flip, fx, fy, fz);
    }

    for (let i = 0; i < batch.ranges.length; i++) {
      const range = batch.ranges[i];
      this.setBlend(range.blend);

      if (!range.clip || !range.clip.count) {
        gl.drawArrays(gl.TRIANGLES, range.start, range.count);
        continue;
      }

      // Clipping goes through the stencil buffer: mark where the clip shape
      // covers, draw the range only there, then unmark. That handles any shape,
      // which matters because the app tile is clipped to a rounded rectangle
      // expressed as a path.
      const clip = range.clip;

      gl.enable(gl.STENCIL_TEST);
      gl.colorMask(false, false, false, false);
      gl.stencilFunc(gl.ALWAYS, 1, 0xff);
      gl.stencilOp(gl.KEEP, gl.KEEP, gl.REPLACE);
      gl.drawArrays(gl.TRIANGLES, clip.start, clip.count);

      gl.colorMask(true, true, true, true);
      gl.stencilFunc(gl.EQUAL, 1, 0xff);
      gl.stencilOp(gl.KEEP, gl.KEEP, gl.KEEP);
      gl.drawArrays(gl.TRIANGLES, range.start, range.count);

      // Reset just the marked region rather than clearing the whole buffer,
      // since several objects on screen are clipped.
      gl.colorMask(false, false, false, false);
      gl.stencilFunc(gl.ALWAYS, 0, 0xff);
      gl.stencilOp(gl.KEEP, gl.KEEP, gl.REPLACE);
      gl.drawArrays(gl.TRIANGLES, clip.start, clip.count);

      gl.colorMask(true, true, true, true);
      gl.disable(gl.STENCIL_TEST);
    }
  }

  // Uploads whatever part of the batch changed. Returns true when the buffer had
  // to be reallocated, which invalidates the attribute pointers recorded against
  // it.
  uploadBatch(batch) {
    if (batch.uploadTo === 0) return false;

    perf.begin("upload");
    const gl = this.gl;

    const needed = batch.buffer.length * VERTEX_STRIDE;
    let grew = false;

    if (needed > batch.capacityBytes) {
      gl.bufferData(gl.ARRAY_BUFFER, batch.buffer.view(), gl.DYNAMIC_DRAW);
      batch.capacityBytes = needed;
      grew = true;
    } else {
      const from = batch.uploadFrom;
      const count = batch.uploadTo - from;
      gl.bufferSubData(
        gl.ARRAY_BUFFER,
        from * VERTEX_STRIDE,
        batch.buffer.viewRange(from, count)
      );
    }

    batch.uploadFrom = 0;
    batch.uploadTo = 0;
    perf.end("upload");

    return grew;
  }

  createVao() {
    return this.vaoExt ? this.vaoExt.create() : null;
  }

  bindVao(vao) {
    if (this.vaoExt) this.vaoExt.bind(vao);
  }

  setBlend(blend) {
    if (blend === this.currentBlend) return;
    this.currentBlend = blend;

    const gl = this.gl;
    if (blend === BLEND_DIFFERENCE) {
      // The map's only use of mix-blend-mode paints white labels over the SQL
      // widget's pie, where "difference" inverts what is beneath. With a
      // premultiplied white source this reproduces it exactly, including the
      // partial coverage along antialiased glyph edges.
      gl.blendFuncSeparate(
        gl.ONE_MINUS_DST_COLOR,
        gl.ONE_MINUS_SRC_ALPHA,
        gl.ONE,
        gl.ONE_MINUS_SRC_ALPHA
      );
    } else {
      gl.blendFuncSeparate(
        gl.ONE,
        gl.ONE_MINUS_SRC_ALPHA,
        gl.ONE,
        gl.ONE_MINUS_SRC_ALPHA
      );
    }
  }

  // -------------------------------------------------------------------------
  // Batch building
  // -------------------------------------------------------------------------

  // Brings a batch up to date by the cheapest route available: re-emitting just
  // the nodes that changed, or regenerating the whole thing when that is not
  // possible.
  refreshBatch(batch, baseStyle) {
    if (!batch.dirty) {
      let ok = true;
      for (let i = 0; i < batch.dirtyNodes.length && ok; i++)
        ok = this.updateNodeInPlace(batch, batch.dirtyNodes[i]);

      batch.dirtyNodes.length = 0;
      if (ok) {
        this.stats.updated++;
        return;
      }
      batch.dirty = true;
    }

    this.buildBatch(batch, baseStyle);
    this.stats.rebuilt++;
  }

  buildBatch(batch, baseStyle) {
    batch.buffer.reset();
    batch.ranges.length = 0;
    batch.picks.length = 0;
    batch.clips.length = 0;
    batch.dirtyNodes.length = 0;
    batch.pickSink = null;

    const state = {
      clip: null,
      blend: BLEND_NORMAL,
      rangeStart: 0,
      changes: 0,
    };

    this.walkForBuild(batch, batch.root, identity(), baseStyle, state, true);
    closeRange(batch, state, batch.buffer.length);

    batch.bounds = computeBatchBounds(batch.buffer);

    // Clip geometry goes after everything that paints, so no draw range can
    // include it by accident.
    appendClipGeometry(batch);
    batch.dirty = false;
    batch.markUpload(0, batch.buffer.length);
  }

  // Re-emits one node's subtree into the slice it already occupies. Returns false
  // when the geometry no longer fits, in which case the caller rebuilds the whole
  // batch instead.
  updateNodeInPlace(batch, node) {
    // A node beneath a hidden ancestor emitted nothing into the batch, so the
    // slice it has recorded is stale - re-emitting into it would stamp vertices
    // over whatever occupies those offsets now. The stat updates that land on
    // an env card's hidden pages did exactly that to the visible page. There is
    // also nothing to draw: the change is already in the scene tree, and the
    // rebuild forced by the ancestor becoming visible will pick it up.
    for (let a = node.parentNode; a && !a.isLayer; a = a.parentNode)
      if (isHidden(a)) return true;

    if (node.subtreeStart === undefined || node.parentMatrix == null)
      return false;

    const total = batch.buffer.length;
    const start = node.subtreeStart;
    const expected = node.subtreeCount;
    if (start + expected > total) return false;

    // Emitting straight into the existing slice keeps every recorded offset
    // meaningful. The tail is only disturbed when the count changes, and that
    // path falls back to a rebuild anyway.
    batch.buffer.length = start;
    batch.pickSink = scratchPicks;
    scratchPicks.length = 0;

    const state = {
      clip: null,
      blend: BLEND_NORMAL,
      rangeStart: start,
      changes: 0,
    };

    // A batch's own transform and opacity are uniforms rather than vertex data,
    // so re-emitting its root has to say so. Walking it as an ordinary node baked
    // both into the geometry on top of the uniforms that were already applying
    // them, which left anything updated in place at its own root doubly
    // transformed and doubly faded from the second frame onwards.
    this.walkForBuild(
      batch,
      node,
      node.parentMatrix,
      node.parentStyle,
      state,
      node === batch.root
    );

    batch.pickSink = null;
    const produced = batch.buffer.length - start;
    batch.buffer.length = total;

    if (produced !== expected) {
      noteBailOut(batch, node, "count", expected, produced);
      return false;
    }

    // A subtree that starts a clip or a blend mode of its own would have to
    // re-cut the batch's draw ranges, which an in-place update cannot do.
    if (state.changes > 0) {
      noteBailOut(batch, node, "changes", expected, produced);
      return false;
    }

    updateDiag.ok++;

    // Picking rejects a batch on its bounding box before testing anything inside
    // it, so the box may be loose but must never be too tight. Growing it to
    // cover what was just emitted keeps that true without rescanning the batch.
    growBounds(batch.bounds, batch.buffer, start, expected);

    batch.markUpload(start, start + expected);
    return true;
  }

  walkForBuild(batch, node, matrix, parentStyle, state, isRoot) {
    if (node.nonRendering) return;
    if (!isRoot && isHidden(node)) return;

    let local = matrix;
    if (!isRoot) {
      const own = transformOf(node);
      if (own) local = multiply(matrix, own);
    }

    let style = resolveStyle(node, parentStyle);

    // The batch root's own opacity is a uniform, so it must not also be baked
    // into the vertex colours here.
    if (isRoot && style.opacity !== parentStyle.opacity)
      style = Object.assign({}, style, { opacity: parentStyle.opacity });

    // Kept so a later change to this node alone can be re-emitted without
    // walking down from the batch root to recover its context.
    node.parentMatrix = matrix;
    node.parentStyle = parentStyle;

    const savedClip = state.clip;
    const savedBlend = state.blend;

    const clip = this.resolveClip(node, local);
    const blend =
      style.blend === "difference" ? BLEND_DIFFERENCE : BLEND_NORMAL;
    const nextClip = clip || state.clip;

    if (nextClip !== state.clip || blend !== state.blend) {
      closeRange(batch, state, batch.buffer.length);
      state.clip = nextClip;
      state.blend = blend;
      state.changes++;
      if (clip) batch.clips.push(clip);
    }

    const subtreeStart = batch.buffer.length;
    emitNode(node, local, style, this.emitContext, batch.buffer);

    // Only geometry that was actually painted can be hit, which is what SVG's
    // default `visiblePainted` means and what the sub-element `data-props`
    // targeting depends on. `pointer-events: none` opts out of that, for
    // decoration drawn over the very thing it decorates.
    //
    // The element's own attribute only — the map has no group that switches it
    // off for a whole subtree, so it is not worth carrying through the style.
    if (
      batch.buffer.length > subtreeStart &&
      node.attrs["pointer-events"] !== "none"
    ) {
      node.matrix = local;
      node.style = style;
      (batch.pickSink || batch.picks).push(node);
    }

    if (drawsChildren(node)) {
      const children = node.children;
      for (let i = 0; i < children.length; i++)
        this.walkForBuild(batch, children[i], local, style, state, false);
    }

    node.subtreeStart = subtreeStart;
    node.subtreeCount = batch.buffer.length - subtreeStart;

    if (state.clip !== savedClip || state.blend !== savedBlend) {
      closeRange(batch, state, batch.buffer.length);
      state.clip = savedClip;
      state.blend = savedBlend;
      state.changes++;
    }
  }

  // Resolves `clip-path: url(#id)` into triangles covering the clipped area,
  // expressed in batch space. Going through geometry rather than an analytic
  // rectangle test is what lets an arbitrary shape clip: the app tile's clip is a
  // rounded rectangle expressed as a <path>, and approximating that by its
  // bounding box would leave the white fade painting over the rounded corners.
  resolveClip(node, matrix) {
    const ref = node.attrs["clip-path"];
    if (!ref) return null;

    const match = /url\(#([^)]+)\)/.exec(ref);
    if (!match) return null;

    const clipPath = this.lookupId(match[1]);
    if (!clipPath) return null;

    const triangles = [];
    for (let i = 0; i < clipPath.children.length; i++) {
      clipShapeTriangles(clipPath.children[i], matrix, triangles);
    }
    if (!triangles.length) return null;

    return { triangles };
  }

  // -------------------------------------------------------------------------
  // Picking and events
  // -------------------------------------------------------------------------

  bindPointerEvents() {
    const canvas = this.canvas;

    canvas.addEventListener("mousemove", this.handleMouseMove);
    canvas.addEventListener("mousedown", this.handlePointerEvent);
    canvas.addEventListener("mouseup", this.handlePointerEvent);
    canvas.addEventListener("click", this.handlePointerEvent);
    canvas.addEventListener("dblclick", this.handlePointerEvent);
    canvas.addEventListener("contextmenu", this.handlePointerEvent);
    canvas.addEventListener("mouseleave", this.handleMouseOut);
  }

  viewCoords(event) {
    if (!this.canvasRect) this.canvasRect = this.canvas.getBoundingClientRect();
    const rect = this.canvasRect;
    return {
      x: event.clientX - rect.left - rect.width / 2,
      y: event.clientY - rect.top - rect.height / 2,
    };
  }

  handlePointerEvent(event) {
    const point = this.viewCoords(event);
    dispatch(event.type, this.pick(point.x, point.y) || this.root, event);
  }

  handleMouseMove(event) {
    const point = this.viewCoords(event);
    const target = this.pick(point.x, point.y) || this.root;

    this.updateHover(target, event);
    dispatch("mousemove", target, event);
  }

  handleMouseOut(event) {
    this.updateHover(this.root, event);
  }

  updateHover(target, nativeEvent) {
    const chain = [];
    let node = target;
    while (node) {
      chain.push(node);
      node = node.parentNode;
    }

    const previous = this.hoverChain;

    // enter and leave do not bubble, so each node is told individually when the
    // pointer crosses into or out of its subtree.
    for (let i = 0; i < previous.length; i++)
      if (chain.indexOf(previous[i]) < 0)
        dispatchDirect("mouseleave", previous[i], nativeEvent);

    for (let i = chain.length - 1; i >= 0; i--)
      if (previous.indexOf(chain[i]) < 0)
        dispatchDirect("mouseenter", chain[i], nativeEvent);

    this.hoverChain = chain;

    // SVG shows <title> as a native tooltip; the canvas carries the same text.
    let title = null;
    for (let i = 0; i < chain.length && title == null; i++)
      title = chain[i].titleText;
    const next = title || "";
    if (this.canvas.title !== next) this.canvas.title = next;
  }

  // Topmost node whose painted geometry contains the view-space point. Batches
  // are rejected on their bounding box first, so the exact tests only run on the
  // handful of objects actually under the pointer.
  pick(viewX, viewY) {
    for (let i = this.layers.length - 1; i >= 0; i--) {
      const layer = this.layers[i];
      if (isHidden(layer)) continue;

      const children = layer.children;
      for (let c = children.length - 1; c >= 0; c--) {
        const node = children[c];
        const batch = node._batch;
        if (!batch || !batch.matrix || !batch.bounds || isHidden(node))
          continue;

        const inverse = invert(batch.matrix);
        const bx = applyX(inverse, viewX, viewY);
        const by = applyY(inverse, viewX, viewY);

        const bounds = batch.bounds;
        if (bx < bounds.minX || bx > bounds.maxX) continue;
        if (by < bounds.minY || by > bounds.maxY) continue;

        const picks = batch.picks;
        for (let p = picks.length - 1; p >= 0; p--) {
          const candidate = picks[p];
          const local = invert(candidate.matrix);
          if (
            hitTestNode(
              candidate,
              applyX(local, bx, by),
              applyY(local, bx, by),
              candidate.style
            )
          )
            return candidate;
        }
      }
    }
    return null;
  }

  // -------------------------------------------------------------------------
  // Lifecycle
  // -------------------------------------------------------------------------

  elementCount() {
    return countNodes(this.root);
  }

  destroy() {
    this.canvas.removeEventListener("webglcontextlost", this.handleContextLost);
    this.canvas.removeEventListener(
      "webglcontextrestored",
      this.handleContextRestored
    );
    this.forEachBatch((batch) => batch.destroy(this.vaoExt));
    if (this.atlas) this.atlas.destroy();
    if (this.canvas.parentNode) this.canvas.parentNode.removeChild(this.canvas);
  }
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function readSuperSample() {
  try {
    const fromUrl = new URLSearchParams(window.location.search).get("ss");
    const n = parseFloat(fromUrl);
    if (n >= 1 && n <= 4) return n;
  } catch (e) {
    // Fall through to the default.
  }
  return SUPERSAMPLE;
}

function createContext(canvas) {
  const attributes = {
    alpha: true,
    antialias: true,
    depth: false,
    stencil: true,
    premultipliedAlpha: true,
    preserveDrawingBuffer: false,
    powerPreference: "high-performance",
  };
  return (
    canvas.getContext("webgl2", attributes) ||
    canvas.getContext("webgl", attributes) ||
    canvas.getContext("experimental-webgl", attributes)
  );
}

// Triangles covering one shape of a clipPath, transformed into batch space.
function clipShapeTriangles(shape, matrix, out) {
  const attrs = shape.attrs;

  switch (shape.tagName) {
    case "rect": {
      const x = num(attrs.x, 0);
      const y = num(attrs.y, 0);
      const w = num(attrs.width, 0);
      const h = num(attrs.height, 0);
      if (w <= 0 || h <= 0) return;
      pushClipTriangle(out, matrix, x, y, x + w, y, x + w, y + h);
      pushClipTriangle(out, matrix, x, y, x + w, y + h, x, y + h);
      return;
    }
    case "circle": {
      const cx = num(attrs.cx, 0);
      const cy = num(attrs.cy, 0);
      const r = num(attrs.r, 0);
      if (r <= 0) return;
      const segments = 64;
      for (let i = 0; i < segments; i++) {
        const a0 = (Math.PI * 2 * i) / segments;
        const a1 = (Math.PI * 2 * (i + 1)) / segments;
        pushClipTriangle(
          out,
          matrix,
          cx,
          cy,
          cx + Math.cos(a0) * r,
          cy + Math.sin(a0) * r,
          cx + Math.cos(a1) * r,
          cy + Math.sin(a1) * r
        );
      }
      return;
    }
    case "path": {
      const subpaths = flattenPath(attrs.d);
      for (let i = 0; i < subpaths.length; i++) {
        const points = subpaths[i].points;
        const indices = triangulate(points);
        for (let t = 0; t < indices.length; t += 3) {
          pushClipTriangle(
            out,
            matrix,
            points[indices[t] * 2],
            points[indices[t] * 2 + 1],
            points[indices[t + 1] * 2],
            points[indices[t + 1] * 2 + 1],
            points[indices[t + 2] * 2],
            points[indices[t + 2] * 2 + 1]
          );
        }
      }
      return;
    }
    default:
      return;
  }
}

function pushClipTriangle(out, m, ax, ay, bx, by, cx, cy) {
  out.push(
    applyX(m, ax, ay),
    applyY(m, ax, ay),
    applyX(m, bx, by),
    applyY(m, bx, by),
    applyX(m, cx, cy),
    applyY(m, cx, cy)
  );
}

// Clip shapes ride along at the end of the batch's own vertex buffer. They are
// only ever drawn into the stencil buffer, so their colour and mode are unused.
function appendClipGeometry(batch) {
  const buffer = batch.buffer;
  for (let i = 0; i < batch.clips.length; i++) {
    const clip = batch.clips[i];
    const points = clip.triangles;
    const count = points.length / 2;

    clip.start = buffer.length;
    clip.count = count;

    buffer.ensure(count);
    // Opaque, even though colour writes are masked off while these are drawn:
    // the shader discards fully transparent fragments, and a discarded fragment
    // never reaches the stencil buffer.
    for (let p = 0; p < points.length; p += 2)
      buffer.push(points[p], points[p + 1], 0, 0, 0xffffffff, 0, 0);
  }
}

function setAttributes(gl, a) {
  gl.enableVertexAttribArray(a.position);
  gl.vertexAttribPointer(a.position, 2, gl.FLOAT, false, VERTEX_STRIDE, 0);
  gl.enableVertexAttribArray(a.uv);
  gl.vertexAttribPointer(a.uv, 2, gl.FLOAT, false, VERTEX_STRIDE, 8);
  gl.enableVertexAttribArray(a.colour);
  gl.vertexAttribPointer(
    a.colour,
    4,
    gl.UNSIGNED_BYTE,
    true,
    VERTEX_STRIDE,
    16
  );
  gl.enableVertexAttribArray(a.extra);
  gl.vertexAttribPointer(a.extra, 2, gl.FLOAT, false, VERTEX_STRIDE, 20);
}

function batchRootOf(node) {
  let current = node;
  while (current.parentNode && !current.parentNode.isLayer)
    current = current.parentNode;
  return current.parentNode ? current : null;
}

function closeRange(batch, state, end) {
  const count = end - state.rangeStart;
  if (count > 0) {
    batch.ranges.push({
      start: state.rangeStart,
      count,
      clip: state.clip,
      blend: state.blend,
    });
  }
  state.rangeStart = end;
}

function growBounds(bounds, buffer, fromVertex, count) {
  if (!bounds) return;
  const data = buffer.f32;
  for (let i = fromVertex; i < fromVertex + count; i++) {
    const x = data[i * FLOATS_PER_VERTEX];
    const y = data[i * FLOATS_PER_VERTEX + 1];
    if (x < bounds.minX) bounds.minX = x;
    if (x > bounds.maxX) bounds.maxX = x;
    if (y < bounds.minY) bounds.minY = y;
    if (y > bounds.maxY) bounds.maxY = y;
  }
}

function computeBatchBounds(buffer) {
  const data = buffer.f32;
  const count = buffer.length;
  if (!count) return null;

  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;

  for (let i = 0; i < count; i++) {
    const x = data[i * FLOATS_PER_VERTEX];
    const y = data[i * FLOATS_PER_VERTEX + 1];
    if (x < minX) minX = x;
    if (x > maxX) maxX = x;
    if (y < minY) minY = y;
    if (y > maxY) maxY = y;
  }

  return { minX, minY, maxX, maxY };
}

function countNodes(node) {
  let total = 1;
  for (let i = 0; i < node.children.length; i++)
    total += countNodes(node.children[i]);
  return total;
}

function numberOr(value, fallback) {
  if (value === undefined || value === null || value === "") return fallback;
  const n = parseFloat(value);
  return isNaN(n) ? fallback : n;
}
